import { z } from 'zod';
import { uuidSchema, localeSchema } from './common.js';
import { aiCapabilitySchema } from './enums.js';
import { mailTemplateKeySchema } from './mail.js';
/** BullMQ queue names shared by API (producer) and worker (consumer). */
export const QUEUES = {
    IMPORT_FETCH: 'import-fetch',
    IMPORT_HEADLESS: 'import-headless',
    MEDIA_PROCESS: 'media-process',
    OCR: 'ocr',
    AI_GENERATION: 'ai-generation',
    SPEECH_TRANSCRIPTION: 'speech-transcription',
    EMAIL: 'email',
    EXPORT: 'export',
    CLEANUP: 'cleanup',
    NOTIFICATIONS: 'notifications',
    CATALOG: 'catalog',
    /** Provider webhooks: delivery events and inbound support mail. */
    MAIL_EVENTS: 'mail-events',
};
export const ALL_QUEUES = Object.values(QUEUES);
export const importFetchJobSchema = z.object({
    importJobId: uuidSchema,
    userId: uuidSchema,
    url: z.string().url(),
    allowAiFallback: z.boolean(),
});
export const mediaProcessJobSchema = z.object({
    assetId: uuidSchema,
    transform: z
        .object({
        rotate: z.number().int(),
        crop: z
            .object({
            x: z.number().int(),
            y: z.number().int(),
            width: z.number().int(),
            height: z.number().int(),
        })
            .nullable(),
    })
        .nullable()
        .default(null),
});
/** OCR queue: extracts text from user-owned image assets via the configured AI provider. */
export const ocrJobSchema = z.object({
    userId: uuidSchema,
    assetIds: z.array(uuidSchema).min(1).max(10),
    /** Optional AI generation to attach the result to (output.ocrText). */
    generationId: uuidSchema.nullable().default(null),
    language: localeSchema.nullable().default(null),
});
/** Headless-browser import fallback (only when SCRAPER_HEADLESS_ENABLED and a browser is installed). */
export const importHeadlessJobSchema = importFetchJobSchema;
export const aiGenerationJobSchema = z.object({
    generationId: uuidSchema,
    userId: uuidSchema,
    capability: aiCapabilitySchema,
});
export const speechTranscriptionJobSchema = z.object({
    generationId: uuidSchema,
    userId: uuidSchema,
    assetId: uuidSchema.nullable(),
    language: z.string().nullable(),
    context: z.enum(['command', 'dictation']),
});
export const emailJobSchema = z.object({
    to: z.string().email(),
    template: mailTemplateKeySchema,
    locale: localeSchema,
    variables: z.record(z.union([z.string(), z.array(z.string())])),
    /** The account the mail is about, for the delivery log. */
    userId: uuidSchema.optional(),
    /**
     * A row of the delivery log that exists already (support answers: the API stores the text,
     * the worker sends it). Without it the worker creates the row.
     */
    messageId: uuidSchema.optional(),
    /** Send these sources instead of the stored template – "send test" of the editor. */
    draft: z
        .object({ subject: z.string(), mjml: z.string(), text: z.string().nullable() })
        .optional(),
});
/** One stored webhook event (`WebhookEvent.id`); the payload stays in the database. */
export const mailEventJobSchema = z.object({ eventId: uuidSchema });
export const exportJobPayloadSchema = z.object({
    exportId: uuidSchema,
    userId: uuidSchema,
    includeImages: z.boolean(),
});
export const cleanupJobSchema = z.object({
    task: z.enum([
        'expired-ai-inputs',
        'expired-audio',
        'expired-exports',
        'expired-import-jobs',
        'orphan-media',
        'trash-purge',
        'expired-sessions',
        'expired-invitations',
        'catalog-retention',
        'shopping-retention',
        'mail-retention',
        'support-housekeeping',
        'receipt-images',
    ]),
});
export const notificationJobSchema = z.object({
    userId: uuidSchema,
    type: z.string(),
    /**
     * What happened, as a key of the message catalogs, with the values for its placeholders. The
     * worker writes title and body from it in the **recipient's** language (push, stored
     * fallback); clients render the key themselves in the reader's current language.
     */
    messageKey: z.string().max(80).optional(),
    params: z.record(z.union([z.string(), z.number()])).optional(),
    /** Only for producers without a key: used as it is. */
    title: z.string().optional(),
    body: z.string().nullable().optional(),
    data: z.record(z.unknown()).nullable(),
    push: z.boolean().default(false),
});
/** Ingredient catalog background work: link backfill and provider lookups for nutrition. */
export const catalogJobSchema = z.discriminatedUnion('task', [
    z.object({ task: z.literal('backfill-links'), requestedById: uuidSchema.nullable() }),
    z.object({
        task: z.literal('resolve-nutrition'),
        ingredientIds: z.array(uuidSchema).min(1).max(200),
    }),
]);
/** Default retry policy for every queue. Individual processors may override. */
export const DEFAULT_JOB_OPTIONS = {
    attempts: 3,
    backoff: { type: 'exponential', delay: 2000 },
    removeOnComplete: { age: 24 * 3600, count: 1000 },
    removeOnFail: { age: 7 * 24 * 3600 },
};
//# sourceMappingURL=jobs.js.map